import React, { useState } from 'react';
import { Container, Row, Col, Card, Button, Badge, Form } from 'react-bootstrap';
import { FaSync, FaRocket, FaCheckCircle, FaStore, FaTruck, FaUsers } from 'react-icons/fa';
import { toast } from 'react-toastify';
import Meta from '../../components/layout/Meta';
import { useRegisterPartnerMutation } from '../../slices/partnerApiSlice';
import Loader from '../../components/layout/Loader';

const PartnersScreen = () => {
    const brandColor = '#b5430f';
    const [formData, setFormData] = useState({
        companyName: '',
        email: '',
        phone: ''
    });

    const [registerPartner, { isLoading }] = useRegisterPartnerMutation();

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.id]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            await registerPartner(formData).unwrap();
            toast.success('¡Solicitud enviada con éxito! Te contactaremos pronto.');
            setFormData({ companyName: '', email: '', phone: '' });
        } catch (err) {
            toast.error(err?.data?.message || err.error || 'Error al enviar la solicitud');
        }
    };

    const features = [
        {
            icon: <FaSync size={40} />,
            title: 'Sincronización Total',
            text: 'Tus productos, precios e inventarios de Siigo Contifico se actualizan automáticamente en tu tienda online.'
        },
        {
            icon: <FaStore size={40} />,
            title: 'Tu Propia Tienda',
            text: 'Obtén un perfil de empresa exclusivo donde tus clientes verán todo tu catálogo organizado.'
        },
        {
            icon: <FaUsers size={40} />,
            title: 'Marketplace Unificado',
            text: 'Tus productos también aparecerán en el catálogo general de HomeSanty, aumentando tu visibilidad.'
        }
    ];

    return (
        <>
            <Meta title="Integración Siigo Contifico" />

            {/* Hero Section */}
            <div className="position-relative py-5 text-white" style={{ backgroundColor: '#1a252f', overflow: 'hidden' }}>
                <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    background: `linear-gradient(135deg, ${brandColor} 0%, #1a252f 100%)`,
                    opacity: 0.9
                }}></div>
                <Container className="position-relative z-1 py-5">
                    <Row className="align-items-center">
                        <Col lg={7} className="mb-5 mb-lg-0">
                            <Badge bg="light" text="dark" className="mb-3 px-3 py-2 fw-bold text-uppercase tracking-wider">
                                Integración Exclusiva con Siigo Contifico
                            </Badge>
                            <h1 className="display-4 fw-bold mb-4">
                                Tu Negocio Online, <br/>
                                <span style={{ color: '#ffcdb2' }}>Integrado y Automatizado</span>
                            </h1>
                            <p className="lead mb-5" style={{ opacity: 0.9, maxWidth: '580px' }}>
                                Publica tus productos en HomeSanty automáticamente desde Siigo Contifico.
                                Gestiona tus propios envíos y contabilidad de forma independiente.
                            </p>
                            <div className="d-flex gap-3 flex-column flex-sm-row">
                                <Button
                                    size="lg"
                                    className="px-5 py-3 fw-bold border-0 shadow-lg btn-demo"
                                    style={{ backgroundColor: brandColor, color: '#fff' }}
                                    onClick={() => document.getElementById('contact-form').scrollIntoView({ behavior: 'smooth' })}
                                >
                                    Agendar Demo
                                </Button>
                                <Button
                                    variant="outline-light"
                                    size="lg"
                                    className="px-5 py-3 fw-bold"
                                >
                                    Ver Video
                                </Button>
                            </div>
                        </Col>
                        <Col lg={5} className="d-none d-lg-block">
                            <div className="p-4 bg-white rounded-4 shadow-lg text-dark text-center position-relative" style={{ opacity: 0.95, transform: 'rotate(2deg)' }}>
                                <div className="position-absolute top-0 start-50 translate-middle bg-white rounded-circle p-3 shadow-sm" style={{ width: '80px', height: '80px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <FaRocket size={40} color={brandColor} />
                                </div>
                                <div className="mt-4 pt-2">
                                    <h4 className="fw-bold">Vende en HomeSanty</h4>
                                    <p className="text-muted small">La plataforma que conecta tu negocio con miles de clientes.</p>
                                    <div className="d-grid gap-2 mt-4 text-start">
                                        <div className="d-flex align-items-center p-3 rounded bg-light border">
                                            <FaStore className="text-primary me-3" size={24} />
                                            <div>
                                                <h6 className="mb-0 fw-bold">Perfil de Empresa</h6>
                                                <small className="text-muted">Tu marca, tus productos</small>
                                            </div>
                                        </div>
                                        <div className="d-flex align-items-center p-3 rounded bg-light border">
                                            <FaTruck className="text-success me-3" size={24} />
                                            <div>
                                                <h6 className="mb-0 fw-bold">Logística Independiente</h6>
                                                <small className="text-muted">Gestiona tus propios envíos</small>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </Col>
                    </Row>
                </Container>
            </div>

            {/* Features Section */}
            <Container className="py-5">
                <Row className="text-center mb-5">
                    <Col lg={8} className="mx-auto">
                        <h2 className="mb-3 fw-bold" style={{ color: brandColor }}>Un Marketplace, Múltiples Beneficios</h2>
                        <p className="text-muted fs-5">
                            Tus productos conviven en un catálogo unificado, pero mantienes el control total de tu negocio.
                        </p>
                    </Col>
                </Row>

                <Row className="g-4 mb-5">
                    {features.map((feature, index) => (
                        <Col md={4} key={index}>
                            <Card className="h-100 border-0 shadow-sm text-center py-5 hover-card transition-all">
                                <Card.Body>
                                    <div className="mb-4 p-3 d-inline-flex align-items-center justify-content-center rounded-circle" style={{ backgroundColor: '#fff5f0', color: brandColor, width: '80px', height: '80px' }}>
                                        {feature.icon}
                                    </div>
                                    <Card.Title className="fw-bold mb-3 fs-4">{feature.title}</Card.Title>
                                    <Card.Text className="text-muted px-3">
                                        {feature.text}
                                    </Card.Text>
                                </Card.Body>
                            </Card>
                        </Col>
                    ))}
                </Row>
            </Container>

            {/* How it works / CTA */}
            <div style={{ backgroundColor: '#f8f9fa' }} className="py-5" id="contact-form">
                <Container>
                    <Row className="align-items-center">
                        <Col lg={6} className="mb-5 mb-lg-0">
                            <h2 className="fw-bold mb-4 display-6" style={{ color: '#2c3e50' }}>
                                ¿Cómo funciona la integración?
                                <br />
                                <span style={{ color: brandColor }}>Simple y Transparente.</span>
                            </h2>
                            <ul className="list-unstyled">
                                {[
                                    'Tus productos se publican automáticamente en HomeSanty.',
                                    'Los clientes pueden comprar productos de varias empresas en un solo sitio.',
                                    'Cada empresa recibe sus pedidos por separado en Siigo.',
                                    'El cliente paga un envío por cada empresa (carrito multi-vendedor).'
                                ].map((item, idx) => (
                                    <li key={idx} className="mb-4 d-flex align-items-start">
                                        <FaCheckCircle className="mt-1 me-3 flex-shrink-0" style={{ color: brandColor, fontSize: '1.2rem' }} />
                                        <span className="fs-5 text-secondary">{item}</span>
                                    </li>
                                ))}
                            </ul>
                            <Button
                                size="lg"
                                className="mt-3 px-5 py-3 border-0 shadow-sm fw-bold"
                                style={{ backgroundColor: brandColor }}
                                onClick={() => document.getElementById('contact-form').scrollIntoView({ behavior: 'smooth' })}
                            >
                                Quiero Integrar mi Negocio
                            </Button>
                        </Col>
                        <Col lg={6}>
                             <Card className="border-0 shadow-lg rounded-4 overflow-hidden">
                                 <div className="p-2" style={{ backgroundColor: brandColor }}></div>
                                <Card.Body className="p-5">
                                    <h3 className="text-center mb-2 fw-bold">Contáctanos</h3>
                                    <p className="text-center text-muted mb-4">
                                        Déjanos tus datos y un especialista en integración Siigo te contactará.
                                    </p>
                                    <Form onSubmit={handleSubmit}>
                                        <Form.Group className="mb-3" controlId="companyName">
                                            <Form.Control 
                                                size="lg" 
                                                type="text" 
                                                placeholder="Nombre de la Empresa" 
                                                className="bg-light border-0"
                                                value={formData.companyName}
                                                onChange={handleChange}
                                                required
                                            />
                                        </Form.Group>
                                        <Form.Group className="mb-3" controlId="email">
                                            <Form.Control 
                                                size="lg" 
                                                type="email" 
                                                placeholder="Correo Electrónico" 
                                                className="bg-light border-0"
                                                value={formData.email}
                                                onChange={handleChange}
                                                required
                                            />
                                        </Form.Group>
                                        <Form.Group className="mb-4" controlId="phone">
                                            <Form.Control 
                                                size="lg" 
                                                type="tel" 
                                                placeholder="Teléfono / WhatsApp" 
                                                className="bg-light border-0"
                                                value={formData.phone}
                                                onChange={handleChange}
                                                required
                                            />
                                        </Form.Group>
                                        <Button 
                                            type="submit"
                                            size="lg" 
                                            className="w-100 fw-bold py-3" 
                                            style={{ backgroundColor: '#2c3e50', border: 'none' }}
                                            disabled={isLoading}
                                        >
                                            {isLoading ? <Loader size="sm" /> : 'Solicitar Información'}
                                        </Button>
                                    </Form>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </div>
            
            <style>
                {`
                    .hover-card {
                        transition: transform 0.3s ease, box-shadow 0.3s ease;
                    }
                    .hover-card:hover {
                        transform: translateY(-10px);
                        box-shadow: 0 1rem 3rem rgba(0,0,0,.175) !important;
                    }
                    .btn-demo {
                        color: #fff !important;
                    }
                    .btn-demo:hover {
                        background-color: #fff !important;
                        color: ${brandColor} !important;
                        transform: translateY(-2px);
                    }
                `}
            </style>
        </>
    );
};

export default PartnersScreen;
